import { useEffect, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────
// WHICH MATCHES HAVE A SCORE TYPED BUT NOT SENT (#556).
//
// The portal is about to start adopting changes a BANEPAD made. Most of them
// are for another court and can be taken silently; one for the match somebody
// is typing into must not be, because replacing the tournament under them
// discards what they entered. That is the failure #537 was opened for.
//
// So the refresher needs to know what would be lost. This is that register.
//
// A MODULE-LEVEL STORE, NOT A CONTEXT, and the reason is the failure mode. A
// provider can be missing — a component rendered outside it would register
// nothing, the refresher would believe nothing was at stake, and the loss would
// be silent. There is no provider to forget here, and no tree position that
// changes the answer.
//
// KEYED BY MATCH ID, because that is what the incoming change can be compared
// against. A component with input for a match it cannot name should say so by
// registering nothing — the refresher treats «I don't know» as «do not touch
// it», which is the safe direction.
// ─────────────────────────────────────────────────────────────────────

/** Match ids with input the operator has not sent. */
const unsaved = new Set<string>();
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

/** Mark/unmark one match. Returns nothing; call it from an effect. */
export function setUnsavedScore(matchId: string, dirty: boolean): void {
  const had = unsaved.has(matchId);
  if (dirty === had) return;
  if (dirty) unsaved.add(matchId);
  else unsaved.delete(matchId);
  announce();
}

/** A snapshot for the refresher to compare an incoming change against. */
export function unsavedScoreIds(): ReadonlySet<string> {
  return new Set(unsaved);
}

/** Tests only — one case must not inherit another's register. */
export function __resetUnsavedScores(): void {
  unsaved.clear();
  announce();
}

export function subscribeUnsavedScores(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Declare that THIS component holds unsent input for `matchId`.
 *
 * Call it unconditionally with a boolean — never conditionally, and never
 * skipped when the score is empty. Unregistering on the way out is what stops a
 * stale entry blocking every future refresh, and it happens in the cleanup so
 * an unmount cannot leave one behind.
 *
 * `matchId` may be empty: a row that cannot name its match registers nothing,
 * and the refresher's «I don't know» path applies.
 */
export function useUnsavedMatchScore(matchId: unknown, dirty: boolean): void {
  const id = String(matchId ?? '');
  useEffect(() => {
    if (!id) return undefined;
    setUnsavedScore(id, dirty);
    return () => setUnsavedScore(id, false);
  }, [id, dirty]);
}

/** Live view of the register, for components that render from it. */
export function useUnsavedScoreIds(): ReadonlySet<string> {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => unsavedScoreIds());
  useEffect(() => subscribeUnsavedScores(() => setIds(unsavedScoreIds())), []);
  return ids;
}
